import { useState, FocusEvent, useRef, useEffect } from 'react';
import PassengerComponent from './passenger.component';
import PopOverComponent, {
	PopOverAlignment
} from '../pop-over/pop-over.component';
import {
	IconNavigationChevronUpComponent,
	IconNavigationChevronDownComponent,
	IconActionUserGroupComponent,
	IconActionUserComponent,
	IconActionUserLargeGroupComponent,
	IconsSize
} from '@vaa-component-lib/component.atom.icons';
import { TextFieldComponent } from '@vaa-component-lib/component.atom.text-field';
import CriteriaTriggerComponent from '../criteria-trigger/criteria-trigger.component';
import { PassengerField, PassengerInput } from './passenger.styles';
import sanitiseString from '../../lib/utils/sanitise-string';
import {
	FlightPartyComposition,
	Passengers
} from '../../lib/utils/composition.service';
import { DEFAULT_PASSENGERS } from '../../lib/utils/constants';
import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';
import { PassengerComposition, PassengerLabels } from './passenger.component';
import {
	TypographyComponent,
	TypographySize,
	TypographyVariant
} from '@vaa-component-lib/component.atom.typography';

export interface PassengerFieldComponentProps {
	label: string | undefined;
	name: string | undefined;
	placeholder?: string | undefined;
	id: string | undefined;
	inputConfig?: {
		composition?: PassengerComposition;
		labels?: PassengerLabels;
	};
	onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
	value: FlightPartyComposition | undefined;
	hasError?: boolean;
	feedback?: string;
}

const PassengerFieldComponent = ({
	label,
	name,
	placeholder,
	id,
	onBlur,
	value,
	hasError,
	feedback,
	inputConfig
}: PassengerFieldComponentProps) => {
	const [showPopOver, setShowPopOver] = useState<boolean>(false);
	const [prettyValue, setPrettyValue] = useState<string>(
		Passengers.prettyFormat(value || DEFAULT_PASSENGERS)
	);
	const [totalPassengers, setTotalPassengers] = useState<number>(
		Passengers.totalPassengers(value || DEFAULT_PASSENGERS)
	);

	const handleFocus = () => {
		setShowPopOver(true);
	};

	const toggleFocus = () => {
		setShowPopOver(!showPopOver);
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
		if (
			e.relatedTarget &&
			ref.current &&
			!ref.current.contains(e.relatedTarget)
		) {
			setShowPopOver(false);
		}
		if (onBlur) {
			onBlur(e);
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (ref.current && !ref.current.contains(event.target as Node)) {
			setShowPopOver(false);
		}
	};

	const popOverId = `popover-${sanitiseString(label || '')}`;

	const ref = useRef<HTMLDivElement>(null);

	const isMobile = useMediaQuery(`(max-width: ${BreakPointToken.SmMax}px)`);

	const getPassengerIcon = (passengerCount: number) => {
		switch (passengerCount) {
			case 1:
				return <IconActionUserComponent size={IconsSize.Sml} />;
			case 2:
				return <IconActionUserGroupComponent size={IconsSize.Sml} />;
			default:
				return <IconActionUserLargeGroupComponent size={IconsSize.Sml} />;
		}
	};

	const popOverFooterContent = (
		<TypographyComponent
			element="p"
			size={TypographySize.Sml}
			variant={TypographyVariant.Body}>
			{prettyValue}
		</TypographyComponent>
	);

	useEffect(() => {
		if (document) {
			if (showPopOver) {
				document.addEventListener('mousedown', handleClickOutside);
			}

			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [showPopOver]);

	useEffect(() => {
		if (value) {
			setPrettyValue(Passengers.prettyFormat(value));
			setTotalPassengers(Passengers.totalPassengers(value));
		}
	}, [value]);

	return (
		<PassengerField data-cy="passenger-field" ref={ref}>
			{isMobile && (
				<CriteriaTriggerComponent
					{...{ label }}
					value={prettyValue}
					onClick={() => setShowPopOver(true)}
					errorMessage={feedback}
					hasError={hasError}
					aria={{ controls: popOverId, expanded: showPopOver }}
					iconChild={getPassengerIcon(totalPassengers)}
				/>
			)}

			<PassengerInput hidden={isMobile}>
				<TextFieldComponent
					{...{
						label,
						placeholder,
						id
					}}
					value={prettyValue}
					autoComplete="off"
					spellCheck={false}
					placeholder={placeholder}
					onFocus={handleFocus}
					onBlur={handleBlur}
					aria-controls={popOverId}
					aria-haspopup="true"
					feedback={showPopOver ? '' : feedback}
					hasError={hasError}
					iconChild={
						showPopOver ? (
							<IconNavigationChevronUpComponent
								aria-label="Close modal"
								// @ts-ignore
								role="img"
							/>
						) : (
							<IconNavigationChevronDownComponent
								aria-label="Open modal"
								// @ts-ignore
								role="img"
							/>
						)
					}
					onIconClick={toggleFocus}
					readonly
				/>
			</PassengerInput>

			<PopOverComponent
				id={popOverId}
				isVisible={showPopOver}
				title="Who's going?"
				footerBtnText="Next"
				footerContent={isMobile && popOverFooterContent}
				footerOnClick={(): void => setShowPopOver(false)}
				onClose={(): void => setShowPopOver(false)}
				alignment={PopOverAlignment.Right}>
				<PassengerComponent
					name={name}
					value={value ? value : DEFAULT_PASSENGERS}
					{...(inputConfig ? { ...inputConfig } : {})}
				/>
			</PopOverComponent>
		</PassengerField>
	);
};

export default PassengerFieldComponent;
